/**
 * Tech-debt #4 — Variable monitoring family (v2.0.1). Install / clear monitors
 * on Device Model variables and report them; `fireMonitorEvents` (exported,
 * used by the SetVariables handler) emits a NotifyEvent when a write crosses a
 * threshold/delta.
 */
import { errorMessage } from '@/lib/errorMessage';
import type { StationId } from '@/domain/types';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { paginateNotifyMonitoringReport } from '@/protocol/v201/actions/notifyMonitoringReport';
import { csmsSession } from '../csmsSessionState';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';
import { findVar, maxReportItems, VALID_MONITOR_TYPES, varValuesRepo } from './shared';

/** setInterval delays are 32-bit; clamp so a huge period doesn't wrap (see reservations). */
const MAX_TIMER_MS = 2_147_483_647;
const PERIODIC_MONITOR_TYPES = ['Periodic', 'PeriodicClockAligned'];

export async function handleSetVariableMonitoring(
  event: CsmsEvent<'SetVariableMonitoring'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Session 22 — install monitors on Device Model variables. v2.0.1-only.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'SetVariableMonitoring is v2.0.1-only', {});
    return;
  }
  const rows = await varValuesRepo.byStation(stationId);
  const setMonitoringResult = event.data.map((d) => {
    const base = {
      type: d.type,
      severity: d.severity,
      component: { name: d.component },
      variable: { name: d.variable },
    };
    if (!rows.some((r) => r.component === d.component && r.variable === d.variable)) {
      return { status: 'UnknownVariable', ...base };
    }
    if (!VALID_MONITOR_TYPES.includes(d.type)) {
      return { status: 'UnsupportedMonitorType', ...base };
    }
    const id = csmsSession.addMonitor(stationId, {
      ...(d.id !== undefined ? { id: d.id } : {}),
      component: d.component,
      variable: d.variable,
      type: d.type,
      value: d.value,
      severity: d.severity,
      transaction: d.transaction,
    });
    // A Periodic monitor fires NotifyEvent(trigger=Periodic) every `value`
    // seconds (PeriodicClockAligned is treated as plain Periodic — clock phase
    // is not modelled). Threshold/Delta monitors fire reactively in
    // fireMonitorEvents instead.
    if (PERIODIC_MONITOR_TYPES.includes(d.type) && d.value > 0) {
      schedulePeriodicMonitor(adapter, stationId, id, d.value);
    }
    return { id, status: 'Accepted', ...base };
  });
  dispatcher.reply(messageId, { setMonitoringResult });
}

export function handleClearVariableMonitoring(
  event: CsmsEvent<'ClearVariableMonitoring'>,
  ctx: CsmsCtx,
): void {
  const clearMonitoringResult = event.ids.map((id) =>
    csmsSession.removeMonitor(ctx.stationId, id)
      ? { id, status: 'Accepted' }
      : { id, status: 'NotFound' },
  );
  ctx.dispatcher.reply(ctx.messageId, { clearMonitoringResult });
}

export async function handleGetMonitoringReport(
  event: CsmsEvent<'GetMonitoringReport'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'GetMonitoringReport is v2.0.1-only', {});
    return;
  }
  const allMon = csmsSession.listMonitors(stationId);
  const matched =
    event.filter.length === 0
      ? allMon
      : allMon.filter((m) =>
          event.filter.some(
            (f) => f.component === m.component && (f.variable === undefined || f.variable === m.variable),
          ),
        );
  if (matched.length === 0) {
    dispatcher.reply(messageId, { status: 'EmptyResultSet' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const rows = await varValuesRepo.byStation(stationId);
  const pages = paginateNotifyMonitoringReport(
    event.requestId,
    matched.map((m) => ({
      component: m.component,
      variable: m.variable,
      id: m.id,
      type: m.type,
      value: m.value,
      severity: m.severity,
      transaction: m.transaction,
    })),
    maxReportItems(rows),
  );
  try {
    for (const page of pages) {
      await adapter.notifyMonitoringReport(page).response;
    }
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}

export function handleSetMonitoringBase(_event: CsmsEvent<'SetMonitoringBase'>, ctx: CsmsCtx): void {
  // We don't differentiate monitoring bases (no FactoryDefault set) — accept
  // and carry on. GenericDeviceModelStatus { Accepted, Rejected }.
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export function handleSetMonitoringLevel(
  _event: CsmsEvent<'SetMonitoringLevel'>,
  ctx: CsmsCtx,
): void {
  // No severity gating modelled — accept. GenericStatus { Accepted, Rejected }.
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

/**
 * Session 22 — when a SetVariables write changes a monitored variable past its
 * threshold/delta, fire NotifyEvent (trigger Alerting / Delta). Periodic
 * monitors are stored + reported but not auto-fired (no timer). Best-effort.
 */
export async function fireMonitorEvents(
  adapter: OcppV201Adapter,
  stationId: StationId,
  component: string,
  variable: string,
  oldValue: string,
  newValue: string,
): Promise<void> {
  const hits = csmsSession
    .listMonitors(stationId)
    .filter((m) => m.component === component && m.variable === variable);
  if (hits.length === 0) return;
  const newNum = Number(newValue);
  const oldNum = Number(oldValue);
  for (const m of hits) {
    let triggered = false;
    let trig: 'Alerting' | 'Delta' = 'Alerting';
    if (m.type === 'UpperThreshold') triggered = Number.isFinite(newNum) && newNum > m.value;
    else if (m.type === 'LowerThreshold') triggered = Number.isFinite(newNum) && newNum < m.value;
    else if (m.type === 'Delta') {
      triggered =
        Number.isFinite(newNum) && Number.isFinite(oldNum) && Math.abs(newNum - oldNum) >= m.value;
      trig = 'Delta';
    }
    if (!triggered) continue;
    const now = new Date(Date.now()).toISOString();
    try {
      await adapter.notifyEvent({
        generatedAt: now,
        seqNo: 0,
        tbc: false,
        eventData: [
          {
            eventId: m.id,
            timestamp: now,
            trigger: trig,
            actualValue: newValue,
            eventNotificationType: 'PreconfiguredMonitor',
            component,
            variable,
            techInfo: `monitor ${m.id} (${m.type} ${m.value})`,
          },
        ],
      }).response;
    } catch (err) {
      useStationStore.setState({ lastError: errorMessage(err) });
    }
  }
}

/**
 * Start the recurring timer for a Periodic monitor: every `periodSec` it reads
 * the variable's current Actual value and emits NotifyEvent(trigger=Periodic).
 * The interval handle lives in csmsSession so ClearVariableMonitoring / station
 * reset cancels it.
 */
function schedulePeriodicMonitor(
  adapter: OcppV201Adapter,
  stationId: StationId,
  monitorId: number,
  periodSec: number,
): void {
  const ms = Math.min(Math.max(periodSec * 1000, 1), MAX_TIMER_MS);
  const handle = setInterval(() => {
    void firePeriodicMonitor(adapter, stationId, monitorId);
  }, ms);
  csmsSession.setMonitorTimer(stationId, monitorId, handle);
}

/** Emit one periodic NotifyEvent for a monitor (no-op if it was removed). */
async function firePeriodicMonitor(
  adapter: OcppV201Adapter,
  stationId: StationId,
  monitorId: number,
): Promise<void> {
  const monitor = csmsSession.listMonitors(stationId).find((m) => m.id === monitorId);
  if (!monitor) return;
  const rows = await varValuesRepo.byStation(stationId);
  const actualValue = findVar(rows, monitor.component, monitor.variable)?.value ?? '';
  const now = new Date(Date.now()).toISOString();
  try {
    await adapter.notifyEvent({
      generatedAt: now,
      seqNo: 0,
      tbc: false,
      eventData: [
        {
          eventId: monitor.id,
          timestamp: now,
          trigger: 'Periodic',
          actualValue,
          eventNotificationType: 'PreconfiguredMonitor',
          component: monitor.component,
          variable: monitor.variable,
          techInfo: `periodic monitor ${monitor.id} (every ${monitor.value}s)`,
        },
      ],
    }).response;
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}
